let isTouchDeviceCache: boolean | null = null;

/**
 * 检查当前设备是否支持触摸操作。
 * 通过检查'ontouchstart'属性和navigator.maxTouchPoints来判断。
 * 第一次调用时执行实际检测并缓存结果，后续调用直接返回缓存结果。
 * 注意：此方法在大多数现代浏览器上表现良好，但在一些老旧或非主流浏览器上可能不准确。
 * @returns {boolean} 如果设备支持触摸操作，则返回true；否则返回false。
 */
export default function isTouchDevice() {
  // 若已缓存结果，则直接返回
  if (isTouchDeviceCache !== null) {
    return isTouchDeviceCache;
  }

  // 确保window和navigator对象存在
  if (!window || !navigator) {
    isTouchDeviceCache = false;
    return false;
  }

  // 使用'ontouchstart'属性和navigator.maxTouchPoints来判断设备是否支持触摸
  // 考虑到某些设备可能支持touch但maxTouchPoints为0的情况，因此需要同时检查两者
  const isTouchSupported =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // 缓存检测结果
  isTouchDeviceCache = isTouchSupported;

  return isTouchSupported;
}

// 拖动事件名称映射
const dragEventNameMap = {
  start: {
    mouse: "mousedown",
    touch: "touchstart",
  },
  move: {
    mouse: "mousemove",
    touch: "touchmove",
  },
  end: {
    mouse: "mouseup",
    touch: "touchend",
  },
};

// 拖动事件绑定
export const dragEvent = {
  bind(
    element: HTMLElement,
    eventName: keyof typeof dragEventNameMap,
    action: (e: MouseEvent | TouchEvent) => void,
    capture: boolean = false
  ): () => void {
    if (isTouchDevice()) {
      element.addEventListener(
        dragEventNameMap[eventName].touch,
        action,
        capture
      );
    }
    element.addEventListener(
      dragEventNameMap[eventName].mouse,
      action,
      capture
    );

    return () => {
      if (isTouchDevice()) {
        element.removeEventListener(
          dragEventNameMap[eventName].touch,
          action,
          capture
        );
      }
      element.removeEventListener(
        dragEventNameMap[eventName].mouse,
        action,
        capture
      );
    };
  },

  start(
    element: HTMLElement,
    action: (e: MouseEvent | TouchEvent) => void,
    capture: boolean = false
  ): () => void {
    return this.bind(element, "start", action, capture);
  },

  move(
    element: HTMLElement,
    action: (e: MouseEvent | TouchEvent) => void,
    capture: boolean = false
  ): () => void {
    return this.bind(element, "move", action, capture);
  },

  end(
    element: HTMLElement,
    action: (e: MouseEvent | TouchEvent) => void,
    capture: boolean = false
  ): () => void {
    return this.bind(element, "end", action, capture);
  },
};
